import React, { isValidElement, Fragment } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Menu, Transition } from "@headlessui/react";
import {
  UserIcon,
  CubeIcon,
  CheckIcon,
  ClockIcon,
  CalendarDaysIcon,
  ChatBubbleLeftRightIcon,
  ListBulletIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  MapPinIcon,
  CalculatorIcon,
  WrenchIcon,
  TagIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import { CheckBadgeIcon } from "@heroicons/react/24/solid";
import { CopyToClipboard } from "../../../utils/Div";
import {
  ReportStatus,
  ReportAppointmentApprovedStatus,
  ReportInvoiceStatus,
  ReportMustBeRectified,
  FullReportAddress,
} from "./utils";
import { ItemListProductStatusTag } from "./item-list-product-status-tag";
import { mapsServiceUrl, mapsServiceIcon, status as statusMap } from "../../../Utils";

const kr = (n) =>
  "kr " + Math.round(Number(n) || 0).toLocaleString("nb-NO");

const formatNOK = (n) =>
  new Intl.NumberFormat("nb-NO", {
    style: "currency",
    currency: "NOK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n) || 0);

/* ----------------------------------------------------------------------- */
/* Lead pills row (status + agreement + picked + executor)                  */
/* ----------------------------------------------------------------------- */
export function ReportLeadPills({ report }) {
  const items = report?.items?.filter((i) => !i.delete) || [];
  const pickedTotal = report?.items_summary?.count ?? items.length;
  const pickedDone =
    report?.items_summary?.picked_count ??
    items.filter((i) => i.picked || i.is_picked).length;
  const executor = report?.executing_employee?.name;

  const hasItemList =
    report?.item_list_product_status &&
    report.item_list_product_status.lines_total > 0;

  return (
    <div className="rvp-pills">
      <ReportStatus report={report} />
      {["new", "ongoing"].includes(report?.status) && (
        <ReportAppointmentApprovedStatus report={report} />
      )}
      {["accomplished"].includes(report?.status) && report?.must_be_rectified && (
        <ReportMustBeRectified rectify={report?.must_be_rectified} />
      )}
      {report?.reportIsSent && (
        <span className="rvp-pill green">
          <CheckBadgeIcon />
          Rapport sendt
        </span>
      )}
      {report?.invoiced && <ReportInvoiceStatus report={report} />}
      {hasItemList && (
        <ItemListProductStatusTag status={report?.item_list_product_status} />
      )}
      {pickedTotal > 0 && (
        <span className="rvp-pill green">
          <CubeIcon />
          {pickedDone}/{pickedTotal} plukket
        </span>
      )}
      {executor && (
        <span className="rvp-pill blue">
          <UserIcon />
          {executor}
        </span>
      )}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Info strip — Kunde / Prosjekt / Utførende / Rapporttype                  */
/* ----------------------------------------------------------------------- */
export function ReportInfoStrip({ report }) {
  const { t } = useTranslation();
  const customer = report?.customer;
  const project = report?.project;

  return (
    <div className="rvp-infostrip">
      <div className="rvp-infocell">
        <div className="rvp-lbl">{t("report.view.summary.customer")}</div>
        <div className="rvp-val link">
          {customer?.id ? (
            <Link to={`/dashboard/customers/${customer.id}`}>
              {customer?.displayTitle || customer?.name || "—"}
            </Link>
          ) : (
            customer?.displayTitle || customer?.name || "—"
          )}
        </div>
        {customer?.mobile && <div className="rvp-sub">{customer.mobile}</div>}
      </div>

      <div className="rvp-infocell">
        <div className="rvp-lbl">{t("report.redesign.project")}</div>
        {project?.id ? (
          <div className="rvp-val link">
            <Link to={`/dashboard/projects/${project.id}`}>
              {project?.displayTitle || project?.name || "—"}
            </Link>
          </div>
        ) : (
          <div className="rvp-val">—</div>
        )}
        {project?.id && (
          <div className="rvp-sub">{t("report.redesign.active")}</div>
        )}
      </div>

      <div className="rvp-infocell">
        <div className="rvp-lbl">{t("report.redesign.executor")}</div>
        <div className="rvp-val">
          {report?.executing_employee?.name || "—"}
        </div>
        <div className="rvp-sub">{t("report.redesign.technician")}</div>
      </div>

      <div className="rvp-infocell">
        <div className="rvp-lbl">{t("report.redesign.report_type")}</div>
        <div className="rvp-val">
          {report?.report_type?.displayTitle || "—"}
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Appointment (Avtale med kunde) — amber accent                            */
/* ----------------------------------------------------------------------- */
export function ReportAppointmentCard({ report, onEdit, onSms }) {
  const { t } = useTranslation();
  const appt = report?.appointment || {};
  const confirmed = appt.approved === true;
  const when = appt.date;

  return (
    <div className={"rvp-card" + (confirmed ? "" : " rvp-accent-amber")}>
      <div className="rvp-appt">
        <div className="rvp-appt-ico">
          <CalendarDaysIcon />
        </div>
        <div className="rvp-appt-body">
          <div className="rvp-t">
            <span>{t("report.redesign.appointment.title")}</span>
            {confirmed ? (
              <span className="rvp-pill green">
                <CheckIcon />
                {t("report.redesign.appointment.confirmed")}
              </span>
            ) : (
              <span className="rvp-pill rose">
                {t("report.redesign.appointment.not_confirmed")}
              </span>
            )}
          </div>
          <div className="rvp-d">
            {confirmed && when
              ? new Date(when).toLocaleString("nb-NO", {
                  dateStyle: "long",
                  timeStyle: "short",
                })
              : t("report.redesign.appointment.empty")}
          </div>
        </div>
        <div className="rvp-appt-acts">
          {onSms && (
            <button type="button" className="rvp-btn rvp-sm" onClick={onSms}>
              <ChatBubbleLeftRightIcon />
              SMS
            </button>
          )}
          <button
            type="button"
            className="rvp-btn rvp-sm rvp-primary"
            onClick={() => onEdit?.("status")}
          >
            <CalendarDaysIcon />
            {t("report.redesign.appointment.set")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Oppdrag card — address + rich description + internal description         */
/* ----------------------------------------------------------------------- */
export function ReportOppdragCard({
  report,
  canEdit,
  onEdit,
  onReportTypeClick,
  children,
}) {
  const { t } = useTranslation();

  return (
    <div className="rvp-card">
      <div className="rvp-card-head">
        <h3>
          <span className="rvp-hi">
            <ListBulletIcon />
          </span>
          {t("report.redesign.oppdrag")}
        </h3>
        {canEdit && (
          <button
            type="button"
            className="rvp-section-link"
            onClick={() => onEdit?.("info")}
          >
            {t("report.redesign.edit")}
          </button>
        )}
      </div>

      <div className="rvp-field-block">
        <div className="rvp-lbl">
          <InformationCircleIcon />
          {t("report.redesign.description")}
        </div>
        {report?.report_type?.displayTitle && (
          <p
            className="rvp-txt"
            style={{
              cursor: onReportTypeClick ? "pointer" : "default",
              fontStyle: "italic",
              marginBottom: 8,
            }}
            onClick={onReportTypeClick}
          >
            {report.report_type.displayTitle}
          </p>
        )}
        {report?.description ? (
          <div
            className="rvp-rich"
            dangerouslySetInnerHTML={{ __html: report.description }}
          />
        ) : (
          <div className="rvp-txt empty">{t("report.redesign.no_description")}</div>
        )}
        {children}
      </div>

      <div className="rvp-field-block rvp-muted-bg">
        <div className="rvp-lbl">
          <ShieldCheckIcon />
          {t("report.redesign.internal")}
          <span className="rvp-fhint">
            ({t("report.redesign.internal_hint")})
          </span>
        </div>
        {report?.internal_description ? (
          <div
            className="rvp-rich"
            dangerouslySetInnerHTML={{ __html: report.internal_description }}
          />
        ) : (
          <div className="rvp-txt empty">{t("report.redesign.no_internal")}</div>
        )}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Finalize card (sidebar)                                                  */
/* ----------------------------------------------------------------------- */
export function ReportFinalizeCard({ report, progress, onFinish }) {
  const { t } = useTranslation();
  const ready = report?.finished;
  const completed = progress?.completed ?? 0;
  const total = progress?.total ?? 0;

  return (
    <div
      className={
        "rvp-card rvp-card-pad rvp-finalize" + (ready ? " rvp-done" : "")
      }
    >
      <div className="rvp-ftitle">
        <span className="rvp-hi">
          <CheckIcon />
        </span>
        {ready
          ? t("report.redesign.finalize.done_title")
          : t("report.redesign.finalize.title")}
      </div>
      <p>
        {ready
          ? t("report.redesign.finalize.done_desc")
          : t("report.redesign.finalize.desc")}
      </p>
      <div className="rvp-prog-row">
        <span>{t("report.redesign.finalize.progress")}</span>
        <span>
          {ready ? t("report.redesign.finalize.done_short") : `${completed} / ${total}`}
        </span>
      </div>
      <div className="rvp-prog">
        <i
          style={{
            width: `${ready ? 100 : Math.min((completed / Math.max(total, 1)) * 100, 100)}%`,
          }}
        />
      </div>
      <div className="rvp-fmeta" />
      <button type="button" className="rvp-btn rvp-green rvp-block" onClick={onFinish}>
        <CheckIcon />
        {ready
          ? t("report.redesign.finalize.view")
          : t("report.redesign.finalize.title")}
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Price card (sidebar) — teal accent                                       */
/* ----------------------------------------------------------------------- */
export function ReportPriceCard({ exVat, incVat, onOpen }) {
  const { t } = useTranslation();
  return (
    <div className="rvp-card rvp-card-pad rvp-accent-teal rvp-price">
      <div className="rvp-pc-top">
        <CalculatorIcon />
        {t("report.redesign.price.title")}
      </div>
      <div className="rvp-pc-amounts">
        <div className="rvp-amt">
          <div className="rvp-k">{t("report.redesign.price.ex_vat")}</div>
          <div className="rvp-v rvp-tnum">{kr(exVat)}</div>
        </div>
        <div className="rvp-amt">
          <div className="rvp-k">{t("report.redesign.price.inc_vat")}</div>
          <div className="rvp-v big rvp-tnum">{kr(incVat)}</div>
        </div>
      </div>
      <button type="button" className="rvp-btn rvp-dark rvp-block" onClick={onOpen}>
        <CalculatorIcon />
        {t("report.redesign.price.open")}
      </button>
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Delivery address card — clickable, opens preferred maps service          */
/* ----------------------------------------------------------------------- */
export function ReportDeliveryAddressCard({ report }) {
  const { t } = useTranslation();
  const preferred = useSelector(
    (state) => state.session?.currentUser?.settings?.preferred_maps_service
  );

  const fullAddress = FullReportAddress(report) || "";
  const hasAddress =
    !!(report?.delivery_address?.address || report?.address) &&
    fullAddress.replace(/,|\s/g, "").length > 0;
  const mapsUrl = hasAddress ? mapsServiceUrl(fullAddress, preferred) : null;
  // Facility/anlegg belongs on CustomerSystemSection — never under delivery address

  const inner = (
    <>
      <span className="rvp-addrc-pin">
        <MapPinIcon />
      </span>
      <div className="rvp-addrc-body">
        <div className="rvp-addrc-lbl">
          {t("report.redesign.address.title")}
        </div>
        <div className="rvp-addrc-val">
          {hasAddress ? fullAddress : t("report.redesign.address.none")}
        </div>
      </div>
      {hasAddress && mapsUrl && (
        <span className="rvp-addrc-go" aria-hidden="true">
          {mapsServiceIcon(preferred) || <ChevronRightIcon />}
        </span>
      )}
    </>
  );

  if (hasAddress && mapsUrl) {
    return (
      <a
        href={mapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="rvp-card rvp-card-pad rvp-addrc rvp-addrc-link"
        aria-label={t("report.redesign.address.open_maps")}
      >
        {inner}
      </a>
    );
  }

  return <div className="rvp-card rvp-card-pad rvp-addrc">{inner}</div>;
}

/* ----------------------------------------------------------------------- */
/* Economy card (sidebar) — reuses GET /reports/:id/summary data            */
/* ----------------------------------------------------------------------- */
export function ReportEconomyCard({ report, summary, loading = false }) {
  const { t } = useTranslation();

  if (loading || !summary || summary.has_orders) return null;

  const Shell = ({ children }) => (
    <div className="rvp-card rvp-card-pad rvp-econ">
      <div className="rvp-econ-head">
        <span className="rvp-hi">
          <CalculatorIcon />
        </span>
        {t("report.redesign.economy.title")}
      </div>
      {children}
    </div>
  );

  if (report?.cost_included_in_project) {
    return (
      <Shell>
        <div className="rvp-econ-note">
          <InformationCircleIcon />
          <div>
            <strong>{t("report.redesign.economy.in_project_title")}</strong>
            <p>{t("report.redesign.economy.in_project_body")}</p>
          </div>
        </div>
      </Shell>
    );
  }

  const invoiced = !!summary.invoiced;
  const costs = summary.costs || {};
  const income = summary.income || {};

  const costLines = invoiced
    ? [
        {
          label: t("report.redesign.economy.order_items_cost"),
          value: costs.order_items,
        },
      ]
    : [
        {
          label: t("report.redesign.economy.item_consumption"),
          value: costs.item_consumption,
        },
        { label: t("report.redesign.economy.hours_cost"), value: costs.hours },
        {
          label: t("report.redesign.economy.car_allowance"),
          value: costs.car_allowance,
        },
      ];

  const incomeLines = invoiced
    ? [
        {
          label: t("report.redesign.economy.order_items_income"),
          value: income.order_items,
        },
      ]
    : [
        {
          label: t("report.redesign.economy.item_sales"),
          value: income.item_consumption,
        },
        { label: t("report.redesign.economy.hours_income"), value: income.hours },
        {
          label: t("report.redesign.economy.car_allowance"),
          value: income.car_allowance,
        },
        income.agreed_price > 0 && {
          label: t("report.redesign.economy.agreed_price"),
          value: income.agreed_price,
        },
        income.additional_hours > 0 && {
          label: t("report.redesign.economy.additional_hours"),
          value: income.additional_hours,
        },
      ].filter(Boolean);

  const hoursTotal =
    summary.hours?.total ??
    summary.total_hours ??
    summary.hours?.registered ??
    0;

  return (
    <Shell>
      <p className="rvp-econ-sub">
        {invoiced
          ? t("report.redesign.economy.sub_actual")
          : t("report.redesign.economy.sub_expected")}
      </p>

      <div className="rvp-econ-group">
        <div className="rvp-econ-gh rvp-econ-cost">
          <WrenchIcon />
          {t("report.redesign.economy.costs")}
        </div>
        {costLines.map((l, i) => (
          <div className="rvp-econ-line" key={i}>
            <span>{l.label}</span>
            <span className="rvp-tnum">{formatNOK(l.value)}</span>
          </div>
        ))}
        <div className="rvp-econ-line total">
          <span>{t("report.redesign.economy.total_cost")}</span>
          <span className="rvp-tnum rvp-cost-val">{formatNOK(costs.total)}</span>
        </div>
      </div>

      <div className="rvp-econ-group">
        <div className="rvp-econ-gh rvp-econ-inc">
          <TagIcon />
          {invoiced
            ? t("report.redesign.economy.income_actual")
            : t("report.redesign.economy.income_expected")}
        </div>
        {incomeLines.map((l, i) => (
          <div className="rvp-econ-line" key={i}>
            <span>{l.label}</span>
            <span className="rvp-tnum">{formatNOK(l.value)}</span>
          </div>
        ))}
        <div className="rvp-econ-line total">
          <span>{t("report.redesign.economy.total_income")}</span>
          <span className="rvp-tnum rvp-inc-val">{formatNOK(income.total)}</span>
        </div>
      </div>

      <div className="rvp-econ-kpis">
        <div className="rvp-kpi">
          <div className="rvp-kpi-v rvp-tnum">{formatNOK(summary.profit)}</div>
          <div className="rvp-kpi-l">{t("report.redesign.economy.profit")}</div>
        </div>
        <div className="rvp-kpi">
          <div className="rvp-kpi-v rvp-tnum">{summary.profit_margin || 0}%</div>
          <div className="rvp-kpi-l">{t("report.redesign.economy.margin")}</div>
        </div>
        <div className="rvp-kpi">
          <div className="rvp-kpi-v rvp-tnum">
            {new Intl.NumberFormat("nb-NO", {
              maximumFractionDigits: 1,
            }).format(Number(hoursTotal) || 0)}
            t
          </div>
          <div className="rvp-kpi-l">{t("report.redesign.economy.hours")}</div>
        </div>
      </div>

      {(summary.kilometers > 0 || summary.items_count > 0) && (
        <div className="rvp-econ-foot">
          {summary.kilometers > 0 && (
            <span>
              {t("report.redesign.economy.distance")}:{" "}
              {new Intl.NumberFormat("nb-NO").format(summary.kilometers)} km
            </span>
          )}
          {summary.items_count > 0 && (
            <span>
              {t("report.redesign.economy.items_count")}: {summary.items_count}
            </span>
          )}
        </div>
      )}
    </Shell>
  );
}

/* ----------------------------------------------------------------------- */
/* Module "section card" (Innhold grid) — compact card matching the new      */
/* design: tinted icon chip, count badge, title, subtitle, progress bar.     */
/* ----------------------------------------------------------------------- */
export function ReportModuleCard({
  icon: Icon,
  accent = "teal",
  title,
  subtitle,
  count,
  total,
  complete = false,
  notRequired = false,
  canEdit = true,
  to,
  onClick,
}) {
  const { t } = useTranslation();

  const hasTotal = typeof total === "number" && total > 0;
  const numericCount = Number(count) || 0;

  let state = "none";
  let fill = 0.06;
  if (notRequired) {
    state = "optional";
    fill = 1;
  } else if (complete) {
    state = "done";
    fill = 1;
  } else if (hasTotal) {
    state = "progress";
    fill = Math.max(0.06, Math.min(numericCount / total, 1));
  } else if (numericCount > 0) {
    state = "progress";
    fill = 0.5;
  }

  const badge = notRequired
    ? t("report.redesign.module.optional")
    : hasTotal
    ? `${numericCount}/${total}`
    : String(numericCount);

  const className =
    `rvp-mcard rvp-mc-${accent} rvp-mc-${state}` +
    (canEdit ? "" : " rvp-mc-readonly");

  const inner = (
    <>
      <div className="rvp-mc-top">
        <span className="rvp-mc-ic">{Icon && <Icon />}</span>
        <span className="rvp-mc-badge">{badge}</span>
      </div>
      <div className="rvp-mc-title">{title}</div>
      {subtitle && <div className="rvp-mc-sub">{subtitle}</div>}
      <div className="rvp-mc-prog" aria-hidden="true">
        <i style={{ width: `${Math.round(fill * 100)}%` }} />
      </div>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={className}
        onClick={(e) => {
          if (!canEdit) e.preventDefault();
        }}
      >
        {inner}
      </Link>
    );
  }

  return (
    <div
      className={className}
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (onClick && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick(e);
        }
      }}
    >
      {inner}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Lead summary card (top of body) — KUNDE / PROSJEKT / UTFØRENDE /          */
/* RAPPORTTYPE. Consumes the same `summaryItems` the header used to render.  */
/* ----------------------------------------------------------------------- */
export function ReportLeadSummary({ items = [] }) {
  const visible = (items || []).filter((it) => it && !it.hidden);
  if (visible.length === 0) return null;

  const renderValue = (item) => {
    if (isValidElement(item.value)) return item.value;
    if (item.path) {
      return (
        <Link to={item.path} className="rvp-ls-link">
          {item.value ?? "—"}
        </Link>
      );
    }
    if (item.action) {
      return (
        <button type="button" className="rvp-ls-link" onClick={item.action}>
          {item.value ?? "—"}
        </button>
      );
    }
    return item.value ?? "—";
  };

  return (
    <div className="rvp-card rvp-card-pad rvp-lead-sum">
      {visible.map((item, i) => (
        <div className="rvp-ls-col" key={i}>
          <div className="rvp-ls-label">{item.label}</div>
          <div className="rvp-ls-val">{renderValue(item)}</div>
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------- */
/* Report header (prototype) — title, subtitle, status badges, actions       */
/* (back icon, "Rediger informasjon" dropdown, kebab "more" menu).           */
/* ----------------------------------------------------------------------- */
function RvpBadge({ tone = "neutral", icon: Icon, dot = false, children }) {
  return (
    <span className={`rvp-badge rvp-badge-${tone}`}>
      {dot && <span className="rvp-badge-dot" aria-hidden="true" />}
      {Icon && <Icon className="rvp-badge-ic" aria-hidden="true" />}
      {children}
    </span>
  );
}

function ReportStatusBadges({ report, t }) {
  const badges = [];

  // 1) Main status
  let statusBadge = null;
  if (report?.finished) {
    if (report?.approved)
      statusBadge = { label: t("Godkjent"), tone: "green", icon: CheckBadgeIcon };
    else if (report?.not_approved)
      statusBadge = { label: t("Ikke godkjent"), tone: "rose" };
    else if (report?.updated_after_process)
      statusBadge = {
        label: t("Oppdatert - venter på behandling"),
        tone: "amber",
      };
    else statusBadge = { label: t("Venter på behandling"), tone: "blue" };
  } else {
    const toneByStatus = {
      draft: "neutral",
      new: "amber",
      ongoing: "blue",
      accomplished: "green",
      processing: "amber",
      processed: "blue",
      closed: "green",
      cancelled: "rose",
      ready_invoice: "rose",
      invoiced: "violet",
      paid: "green",
    };
    const s = report?.status;
    const mapped = statusMap?.[s];
    if (mapped) {
      statusBadge = {
        label: t(mapped.label),
        tone: toneByStatus[s] || "neutral",
        dot: true,
      };
    }
  }
  if (statusBadge)
    badges.push(
      <RvpBadge
        key="status"
        tone={statusBadge.tone}
        icon={statusBadge.icon}
        dot={statusBadge.dot}
      >
        {statusBadge.label}
      </RvpBadge>
    );

  // 2) Appointment (agreement reports that are still open)
  if (
    report?.report_type?.agreement &&
    ["new", "ongoing"].includes(report?.status)
  ) {
    const appt = report?.appointment || {};
    let apptBadge;
    if (appt.approved)
      apptBadge = {
        label: t("report.redesign.badge.confirmed"),
        tone: "green",
        icon: CheckBadgeIcon,
      };
    else if (appt.has_agreement)
      apptBadge = {
        label: t("report.redesign.badge.not_confirmed"),
        tone: "amber",
        icon: ClockIcon,
      };
    else
      apptBadge = {
        label: t("report.redesign.badge.not_agreed"),
        tone: "neutral",
        icon: ClockIcon,
      };
    badges.push(
      <RvpBadge key="appt" tone={apptBadge.tone} icon={apptBadge.icon}>
        {apptBadge.label}
      </RvpBadge>
    );
  }

  // 3) Pick status (warehouse) — reuse the canonical tag
  if (report?.item_list_product_status?.lines_total > 0) {
    badges.push(
      <ItemListProductStatusTag
        key="pick"
        status={report.item_list_product_status}
        compact
      />
    );
  }

  // 4) Must be rectified
  if (report?.must_be_rectified) {
    badges.push(
      <RvpBadge key="rectify" tone="rose">
        {t("report.redesign.badge.must_rectify")}
      </RvpBadge>
    );
  }

  // 5) Invoiced
  if (report?.invoiced) {
    badges.push(
      <RvpBadge key="invoice" tone="violet">
        {t("report.redesign.badge.invoiced")}
      </RvpBadge>
    );
  }

  // 6) Executor
  if (report?.executing_employee?.name) {
    badges.push(
      <RvpBadge key="exec" tone="neutral" icon={UserIcon}>
        {report.executing_employee.name}
      </RvpBadge>
    );
  }

  if (badges.length === 0) return null;
  return <div className="rvp-hdr-badges">{badges}</div>;
}

export function ReportHeader({
  report,
  title,
  subtitle,
  editMenu = null,
  menuActions = [],
  backTo,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const visibleMenu = (menuActions || []).filter((a) => a && a.hidden === false);

  return (
    <div className="rvp-scope rvp-hdr">
      <div className="rvp-hdr-row">
        <div className="rvp-hdr-titles">
          <h1 className="rvp-hdr-title">
            <CopyToClipboard value={title}>{title}</CopyToClipboard>
          </h1>
          {subtitle && <p className="rvp-hdr-sub">{subtitle}</p>}
        </div>

        <div className="rvp-hdr-actions">
          {backTo && (
            <button
              type="button"
              className="rvp-hdr-iconbtn"
              aria-label={t("Back")}
              onClick={() => navigate(backTo, { replace: true })}
            >
              <ArrowLeftIcon />
            </button>
          )}

          {editMenu}

          {visibleMenu.length > 0 && (
            <Menu as="div" className="rvp-hdr-menu">
              <Menu.Button
                className="rvp-hdr-iconbtn"
                aria-label={t("More")}
              >
                <EllipsisVerticalIcon />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="rvp-hdr-menu-items">
                  {visibleMenu.map((action, index) => (
                    <Menu.Item key={index} disabled={action.disabled}>
                      {({ active }) => (
                        <button
                          type="button"
                          onClick={action.action}
                          disabled={action.disabled}
                          className={
                            "rvp-hdr-menu-item" + (active ? " is-active" : "")
                          }
                        >
                          {action.icon && (
                            <span className="rvp-hdr-menu-ic">
                              <action.icon aria-hidden="true" />
                            </span>
                          )}
                          <span className="rvp-hdr-menu-txt">
                            <span className="rvp-hdr-menu-label">
                              {action.label}
                            </span>
                            {action.description ? (
                              <span className="rvp-hdr-menu-desc">
                                {action.description}
                              </span>
                            ) : null}
                          </span>
                          <ChevronRightIcon
                            className="rvp-hdr-menu-go"
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </Menu>
          )}
        </div>
      </div>

      <ReportStatusBadges report={report} t={t} />
    </div>
  );
}

export { ClockIcon as RvpClockIcon };
